/**
 * Proyecciones que el portal deriva del log (ver la nota de `log.ts`: por qué
 * se recalculan acá en vez de importar `@scout/stats` o pedirle al API).
 *
 * Las formas de salida son las de `@scout/stats` (`RotationReport`,
 * `SetterDistribution`, `ZoneMatrix`, `ReceptionImpact`, `ScorePoint`) para
 * que el día que el API las publique se pueda cambiar el origen del dato sin
 * tocar ni un componente. Los criterios de conteo también son los de allá:
 * están documentados en cada bloque.
 *
 * La sección 6 es la excepción y está marcada: no calcula nada, LEE el desglose
 * de puntos que `buildMatchReport` ya publica en `GET /report`.
 *
 * Todo puro y sin DOM.
 */
import type { TeamSide } from '@scout/core';
import type { EfficiencyConfig } from './efficiency';
import { selectByPreviousRally, type PreviousRallyFilter } from './rally-filter';
import {
  EVALUATIONS,
  SIDES,
  closedRallies,
  countTotal,
  efficiencyFor,
  emptyCounts,
  filterBySets,
  normalizeLog,
  perfectPctFor,
  ratio,
  readMatchMeta,
  splitRallies,
  type Evaluation,
  type EvaluationCounts,
  type MatchMeta,
  type NormalizedEvent,
  type TeamMeta,
  type TouchEvent,
} from './log';

const ROTATIONS = 6;

/** Rota una formación: P2→P1, P3→P2 … P1→P6 (misma regla que el motor). */
export function rotateForward(rotation: readonly number[]): number[] {
  if (rotation.length <= 1) return [...rotation];
  const first = rotation[0] as number;
  return [...rotation.slice(1), first];
}

/** `setsIncluded` normalizado, igual que en `MatchReport`. */
function setsIncludedOf(sets: readonly number[] | undefined): number[] | 'all' {
  return sets && sets.length > 0 ? [...sets].sort((a, b) => a - b) : 'all';
}

export interface ProjectionOptions {
  sets?: readonly number[];
  /**
   * Convención de eficiencia (`lib/efficiency.ts`); sin ella rige el preset
   * estándar, número por número el de siempre. Sólo cambia divisiones
   * publicadas (eficiencia/positividad), nunca conteos ni métricas de rally —
   * mismo alcance que `ReportOptions.efficiency` en `@scout/stats`.
   */
  efficiency?: EfficiencyConfig;
  /**
   * "One Rally Before" (`lib/rally-filter.ts`): sólo los rallies cuyo rally
   * anterior (mismo set) cumple la condición. Aplica a armador, zonas y
   * recepción — el resumen lo hace el API con el mismo filtro por query.
   */
  previousRally?: PreviousRallyFilter;
}

// ===========================================================================
// 1. Rotaciones (sideout / break por posición del armador)
// ===========================================================================

export interface RotationLine {
  /** Posición del armador, 1..6 (1 = armador en zona 1, sacando). */
  rotation: number;
  label: string;
  rallies: number;
  ralliesServed: number;
  ralliesReceived: number;
  pointsWon: number;
  pointsLost: number;
  netPoints: number;
  breakPoints: number;
  sideoutPoints: number;
  sideoutPct: number;
  breakPct: number;
}

export type UntrackedReason = 'no-lineup' | 'no-setter';

export interface TeamRotationReport {
  name: string;
  tracked: boolean;
  untrackedReason?: UntrackedReason;
  rotations: RotationLine[];
  unassignedRallies: number;
}

export interface RotationReport {
  matchId: string;
  generatedAt: string;
  setsIncluded: number[] | 'all';
  home: TeamRotationReport;
  away: TeamRotationReport;
}

interface RotationAcc {
  rallies: number;
  ralliesServed: number;
  ralliesReceived: number;
  pointsWon: number;
  pointsLost: number;
  breakPoints: number;
  sideoutPoints: number;
}

interface TrackState {
  rotation: number[];
  setter: number | null;
  seenLineup: boolean;
  everTracked: boolean;
  unassigned: number;
  lines: RotationAcc[];
}

function emptyRotationAcc(): RotationAcc {
  return {
    rallies: 0,
    ralliesServed: 0,
    ralliesReceived: 0,
    pointsWon: 0,
    pointsLost: 0,
    breakPoints: 0,
    sideoutPoints: 0,
  };
}

function newTrackState(): TrackState {
  return {
    rotation: [],
    setter: null,
    seenLineup: false,
    everTracked: false,
    unassigned: 0,
    lines: Array.from({ length: ROTATIONS }, emptyRotationAcc),
  };
}

function setterIndexOf(st: TrackState): number | null {
  if (st.setter === null) return null;
  const idx = st.rotation.indexOf(st.setter);
  return idx >= 0 && idx < ROTATIONS ? idx : null;
}

/** `setterPosition` manda; si no viene, el rol declarado en el plantel. */
function findSetter(
  rotation: readonly number[],
  setterPosition: number | undefined,
  team: TeamMeta,
): number | null {
  if (setterPosition !== undefined) {
    const byPosition = rotation[setterPosition - 1];
    if (byPosition !== undefined) return byPosition;
  }
  return rotation.find((n) => team.roles.get(n) === 'setter') ?? null;
}

/**
 * Rotación = posición del armador (convención DataVolley). El rally se
 * atribuye a la rotación VIGENTE MIENTRAS SE JUGÓ; recién después rota el que
 * hizo sideout. Sin lineup o sin armador identificable el equipo queda
 * `tracked: false`: mejor decir "no se puede reconstruir" que mostrar ceros.
 */
export function buildRotationReport(
  all: readonly NormalizedEvent[],
  opts: ProjectionOptions = {},
  rotationEnabled = true,
): RotationReport {
  const meta = readMatchMeta(all);
  const rallies = splitRallies(filterBySets(all, opts.sets));
  const state: Record<TeamSide, TrackState> = { home: newTrackState(), away: newTrackState() };

  for (const rally of rallies) {
    for (const event of rally.events) {
      if (event.type === 'lineup_set') {
        const st = state[event.payload.team];
        st.rotation = [...event.payload.rotation];
        st.seenLineup = true;
        st.setter = findSetter(
          st.rotation,
          event.payload.setterPosition,
          meta.teams[event.payload.team],
        );
        if (setterIndexOf(st) !== null) st.everTracked = true;
      } else if (event.type === 'substitution') {
        const st = state[event.payload.team];
        const { playerOut, playerIn } = event.payload;
        st.rotation = st.rotation.map((n) => (n === playerOut ? playerIn : n));
        // El que entra hereda la ranura del armador (doble cambio).
        if (st.setter === playerOut) st.setter = playerIn;
      }
    }

    if (!rally.closed || rally.winner === null) continue;
    const winner = rally.winner;

    for (const side of SIDES) {
      const st = state[side];
      const idx = setterIndexOf(st);
      if (idx === null) {
        st.unassigned += 1;
        continue;
      }
      const line = st.lines[idx];
      if (!line) continue;
      line.rallies += 1;
      if (winner === side) line.pointsWon += 1;
      else line.pointsLost += 1;
      if (rally.server !== null) {
        if (rally.server === side) {
          line.ralliesServed += 1;
          if (winner === side) line.breakPoints += 1;
        } else {
          line.ralliesReceived += 1;
          if (winner === side) line.sideoutPoints += 1;
        }
      }
    }

    if (rotationEnabled && rally.server !== null && rally.server !== winner) {
      const st = state[winner];
      st.rotation = rotateForward(st.rotation);
    }
  }

  return {
    matchId: meta.matchId,
    generatedAt: new Date().toISOString(),
    setsIncluded: setsIncludedOf(opts.sets),
    home: finalizeRotationTeam(state.home, meta.teams.home.name),
    away: finalizeRotationTeam(state.away, meta.teams.away.name),
  };
}

function finalizeRotationTeam(st: TrackState, name: string): TeamRotationReport {
  if (!st.everTracked) {
    return {
      name,
      tracked: false,
      untrackedReason: st.seenLineup ? 'no-setter' : 'no-lineup',
      rotations: [],
      unassignedRallies: st.unassigned,
    };
  }
  return {
    name,
    tracked: true,
    rotations: st.lines.map((acc, i) => ({
      rotation: i + 1,
      label: `P${i + 1}`,
      rallies: acc.rallies,
      ralliesServed: acc.ralliesServed,
      ralliesReceived: acc.ralliesReceived,
      pointsWon: acc.pointsWon,
      pointsLost: acc.pointsLost,
      netPoints: acc.pointsWon - acc.pointsLost,
      breakPoints: acc.breakPoints,
      sideoutPoints: acc.sideoutPoints,
      sideoutPct: ratio(acc.sideoutPoints, acc.ralliesReceived),
      breakPct: ratio(acc.breakPoints, acc.ralliesServed),
    })),
    unassignedRallies: st.unassigned,
  };
}

// ===========================================================================
// 2. Distribución del armador (por grado de recepción y por FASE)
// ===========================================================================

/**
 * Las dos fases del armador, con los mismos criterios que `@scout/stats`:
 * armar de primera intención (el balón viene de la recepción y están todas las
 * opciones abiertas) no es lo mismo que armar un contraataque re-armado tras
 * defensa, donde suele quedar una sola opción y el bloqueo rival ya está
 * parado. Juntas tapan justo la decisión que el entrenador quiere ver.
 *
 * Un ataque cae en UNA fase y sólo una, así que
 * `sideout.total + transition.total` = todos los ataques del equipo.
 */

/** Grados de recepción que dejan armar (`/` y `=` no dejan). */
export const SETTABLE_GRADES: readonly Evaluation[] = ['#', '+', '!', '-'];

export type TargetSource = 'combo' | 'startZone' | 'endZone' | 'unknown';

export type SetterPhase = 'sideout' | 'transition';

export const SETTER_PHASES: readonly SetterPhase[] = ['sideout', 'transition'];

export interface SetterTargetLine {
  key: string;
  source: TargetSource;
  count: number;
  pct: number;
  attackEfficiency: number;
}

export interface SetterGradeLine {
  total: number;
  byTarget: SetterTargetLine[];
}

/** La distribución de UNA fase: sin condicionar y condicionada al grado. */
export interface SetterPhaseDistribution {
  /** Todos los ataques de la fase (haya o no recepción propia codificada). */
  total: number;
  /** Destinos de esos `total` ataques, mismo orden y criterio de siempre. */
  byTarget: SetterTargetLine[];
  /** Los mismos ataques abiertos por grado de recepción del rally. */
  byReceptionGrade: Record<string, SetterGradeLine>;
  /** Σ de los 4 grados; `total − withReception` son los ataques sin recepción armable. */
  withReception: number;
}

export interface TeamSetterDistribution {
  name: string;
  /** Distribución JUNTA (sideout + transición) condicionada al grado. */
  byReceptionGrade: Record<string, SetterGradeLine>;
  /** Primera intención: el ataque que sale de la recepción. */
  sideout: SetterPhaseDistribution;
  /** Contraataque re-armado tras defensa propia (incluye los rallies sacados). */
  transition: SetterPhaseDistribution;
}

export interface SetterDistribution {
  matchId: string;
  generatedAt: string;
  setsIncluded: number[] | 'all';
  home: TeamSetterDistribution;
  away: TeamSetterDistribution;
}

/** Destino de un ataque: combinación → zona de salida → zona de llegada. */
export function attackTarget(touch: TouchEvent): { key: string; source: TargetSource } {
  const p = touch.payload;
  if (p.combo) return { key: p.combo, source: 'combo' };
  if (p.startZone !== undefined) return { key: `Z${p.startZone}`, source: 'startZone' };
  if (p.endZone !== undefined) return { key: `E${p.endZone}`, source: 'endZone' };
  return { key: 'unknown', source: 'unknown' };
}

interface TargetAcc {
  key: string;
  source: TargetSource;
  counts: EvaluationCounts;
  total: number;
}

/** Destinos de un conjunto de ataques: clave → celda. */
type TargetAccs = Map<string, TargetAcc>;

/** Acumulador de una fase: los ataques sueltos y los abiertos por grado. */
interface PhaseAcc {
  all: TargetAccs;
  byGrade: Map<string, TargetAccs>;
}

function newPhaseAcc(): PhaseAcc {
  const byGrade = new Map<string, TargetAccs>();
  for (const grade of SETTABLE_GRADES) byGrade.set(grade, new Map());
  return { all: new Map(), byGrade };
}

function newTeamAcc(): Record<SetterPhase, PhaseAcc> {
  return { sideout: newPhaseAcc(), transition: newPhaseAcc() };
}

function addAttack(targets: TargetAccs, touch: TouchEvent): void {
  const { key, source } = attackTarget(touch);
  let cell = targets.get(key);
  if (!cell) {
    cell = { key, source, counts: emptyCounts(), total: 0 };
    targets.set(key, cell);
  }
  cell.counts[touch.payload.evaluation] += 1;
  cell.total += 1;
}

/**
 * Los ataques de `side` en el rally, cada uno con la fase en la que se armó:
 * es `sideout` si el equipo ya recibió y NO tocó `dig`/`freeball` en el medio;
 * en cualquier otro caso es `transition` (contraataque tras defensa propia,
 * ataque de un rally que el equipo sacaba, o ataque sin recepción previa).
 */
function attacksByPhase(
  rally: { touches: readonly TouchEvent[] },
  side: TeamSide,
): Array<{ touch: TouchEvent; phase: SetterPhase }> {
  const out: Array<{ touch: TouchEvent; phase: SetterPhase }> = [];
  let received = false;
  let rearmed = false;
  for (const t of rally.touches) {
    if (t.payload.team !== side) continue;
    const skill = t.payload.skill;
    if (skill === 'reception') {
      // Una segunda recepción (overpass) no re-abre la primera intención.
      received = true;
      continue;
    }
    if (skill === 'dig' || skill === 'freeball') {
      rearmed = true;
      continue;
    }
    if (skill !== 'attack') continue;
    out.push({ touch: t, phase: received && !rearmed ? 'sideout' : 'transition' });
  }
  return out;
}

export function buildSetterDistribution(
  all: readonly NormalizedEvent[],
  opts: ProjectionOptions = {},
): SetterDistribution {
  const meta = readMatchMeta(all);
  const rallies = selectByPreviousRally(
    splitRallies(filterBySets(all, opts.sets)),
    opts.previousRally,
  ).rallies;

  const acc: Record<TeamSide, Record<SetterPhase, PhaseAcc>> = {
    home: newTeamAcc(),
    away: newTeamAcc(),
  };

  for (const rally of rallies) {
    for (const side of SIDES) {
      // Grado de la PRIMERA recepción del equipo (la que define la
      // construcción). Si no recibió, o si la recepción fue `/` o `=`, no hay
      // construcción que condicionar — pero los ataques igual existen y se
      // cuentan en su fase.
      const reception = rally.touches.find(
        (t) => t.payload.team === side && t.payload.skill === 'reception',
      );
      const grade = reception?.payload.evaluation;

      for (const { touch, phase } of attacksByPhase(rally, side)) {
        const phaseAcc = acc[side][phase];
        addAttack(phaseAcc.all, touch);
        const targets = grade === undefined ? undefined : phaseAcc.byGrade.get(grade);
        if (targets) addAttack(targets, touch);
      }
    }
  }

  return {
    matchId: meta.matchId,
    generatedAt: new Date().toISOString(),
    setsIncluded: setsIncludedOf(opts.sets),
    home: finalizeSetter(acc.home, meta.teams.home.name, opts.efficiency),
    away: finalizeSetter(acc.away, meta.teams.away.name, opts.efficiency),
  };
}

// ---------------------------------------------------------------------------
// 2b. La distribución del armador SOBRE LA CANCHA (por zona de salida)
// ---------------------------------------------------------------------------

/**
 * Las tablas del armador se agrupan por DESTINO (`attackTarget`: combinación →
 * zona de salida → zona de llegada), que es la lectura de DataVolley. Para
 * dibujar la distribución sobre la cancha —R# / R+ por zona, como la muestra
 * VolleyStation— hace falta otra clave: la ZONA DE SALIDA del ataque, que en
 * el contrato viaja aparte (`startZone`) aunque el ataque tenga combinación.
 *
 * Mismo recorte que `buildSetterDistribution` (sets, "One Rally Before"),
 * mismas fases y el mismo grado de recepción: lo único que cambia es la clave.
 * Lo que no tiene zona de salida 1..6 va a `other`, así el total cierra con la
 * tabla.
 */
export type SetterCourtView = 'all' | SetterPhase;
export type SetterCourtGrade = 'all' | '#' | '+' | '!' | '-';

export const SETTER_COURT_GRADES: readonly SetterCourtGrade[] = ['all', '#', '+', '!', '-'];

export interface SetterCourtZone {
  zone: number;
  count: number;
  /** Proporción sobre el total de la muestra (incluye `other`). */
  share: number;
  /** Eficiencia de ataque de la zona, con la convención activa. */
  efficiency: number;
}

export interface SetterCourtLine {
  total: number;
  other: number;
  zones: SetterCourtZone[];
}

export interface TeamSetterCourt {
  name: string;
  views: Record<SetterCourtView, Record<SetterCourtGrade, SetterCourtLine>>;
}

export interface SetterCourt {
  home: TeamSetterCourt;
  away: TeamSetterCourt;
}

type ZoneCountsAcc = { zones: Map<number, EvaluationCounts>; total: number; other: number };

function newZoneCountsAcc(): ZoneCountsAcc {
  return { zones: new Map(), total: 0, other: 0 };
}

function addAttackZone(acc: ZoneCountsAcc, touch: TouchEvent): void {
  acc.total += 1;
  const zone = touch.payload.startZone;
  if (zone === undefined || zone < 1 || zone > 6) {
    acc.other += 1;
    return;
  }
  let counts = acc.zones.get(zone);
  if (!counts) {
    counts = emptyCounts();
    acc.zones.set(zone, counts);
  }
  counts[touch.payload.evaluation] += 1;
}

function finalizeZoneCounts(acc: ZoneCountsAcc, efficiency?: EfficiencyConfig): SetterCourtLine {
  const zones: SetterCourtZone[] = [];
  for (const [zone, counts] of acc.zones) {
    const count = countTotal(counts);
    zones.push({
      zone,
      count,
      share: ratio(count, acc.total),
      efficiency: efficiencyFor('attack', counts, count, efficiency),
    });
  }
  zones.sort((a, b) => a.zone - b.zone);
  return { total: acc.total, other: acc.other, zones };
}

export function buildSetterCourt(
  all: readonly NormalizedEvent[],
  opts: ProjectionOptions = {},
): SetterCourt {
  const meta = readMatchMeta(all);
  const rallies = selectByPreviousRally(
    splitRallies(filterBySets(all, opts.sets)),
    opts.previousRally,
  ).rallies;

  const views: SetterCourtView[] = ['all', 'sideout', 'transition'];
  const make = () => {
    const out = {} as Record<SetterCourtView, Record<SetterCourtGrade, ZoneCountsAcc>>;
    for (const view of views) {
      out[view] = {} as Record<SetterCourtGrade, ZoneCountsAcc>;
      for (const grade of SETTER_COURT_GRADES) out[view][grade] = newZoneCountsAcc();
    }
    return out;
  };
  const acc: Record<TeamSide, ReturnType<typeof make>> = { home: make(), away: make() };

  for (const rally of rallies) {
    for (const side of SIDES) {
      const reception = rally.touches.find(
        (t) => t.payload.team === side && t.payload.skill === 'reception',
      );
      const grade = reception?.payload.evaluation;
      const gradeKey: SetterCourtGrade | null =
        grade === '#' || grade === '+' || grade === '!' || grade === '-' ? grade : null;
      for (const { touch, phase } of attacksByPhase(rally, side)) {
        for (const view of ['all', phase] as const) {
          addAttackZone(acc[side][view].all, touch);
          if (gradeKey !== null) addAttackZone(acc[side][view][gradeKey], touch);
        }
      }
    }
  }

  const finalize = (side: TeamSide, name: string): TeamSetterCourt => {
    const out = {} as TeamSetterCourt['views'];
    for (const view of views) {
      out[view] = {} as Record<SetterCourtGrade, SetterCourtLine>;
      for (const grade of SETTER_COURT_GRADES) {
        out[view][grade] = finalizeZoneCounts(acc[side][view][grade], opts.efficiency);
      }
    }
    return { name, views: out };
  };

  return {
    home: finalize('home', meta.teams.home.name),
    away: finalize('away', meta.teams.away.name),
  };
}

/** Una tabla de destinos: total, porcentajes y eficiencia por destino. */
function finalizeTargets(targets: TargetAccs, efficiency?: EfficiencyConfig): SetterGradeLine {
  let total = 0;
  for (const cell of targets.values()) total += cell.total;
  return {
    total,
    byTarget: [...targets.values()]
      .map((c) => ({
        key: c.key,
        source: c.source,
        count: c.total,
        pct: ratio(c.total, total),
        attackEfficiency: efficiencyFor('attack', c.counts, c.total, efficiency),
      }))
      // Desempate por código, NO con `localeCompare`: la collation depende
      // del ICU del navegador (ordena 'unknown' antes que 'Z4' porque ignora
      // mayúsculas en el primer nivel), así que el orden no era estable ni
      // entre browsers ni contra @scout/stats, que compara con `<`. La tabla
      // del armador tiene que salir igual en la pantalla y en el reporte.
      .sort((a, b) => b.count - a.count || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)),
  };
}

/** Une dos conjuntos de destinos (las dos fases) en uno solo. */
function mergeTargets(a: TargetAccs, b: TargetAccs): TargetAccs {
  const out: TargetAccs = new Map();
  for (const source of [a, b]) {
    for (const cell of source.values()) {
      const existing = out.get(cell.key);
      if (!existing) {
        out.set(cell.key, { ...cell, counts: { ...cell.counts } });
        continue;
      }
      for (const e of EVALUATIONS) existing.counts[e] += cell.counts[e];
      existing.total += cell.total;
    }
  }
  return out;
}

function finalizePhase(phase: PhaseAcc, efficiency?: EfficiencyConfig): SetterPhaseDistribution {
  const all = finalizeTargets(phase.all, efficiency);
  const byReceptionGrade: Record<string, SetterGradeLine> = {};
  let withReception = 0;
  for (const grade of SETTABLE_GRADES) {
    const line = finalizeTargets(phase.byGrade.get(grade) ?? new Map(), efficiency);
    withReception += line.total;
    byReceptionGrade[grade] = line;
  }
  return { total: all.total, byTarget: all.byTarget, byReceptionGrade, withReception };
}

function finalizeSetter(
  phases: Record<SetterPhase, PhaseAcc>,
  name: string,
  efficiency?: EfficiencyConfig,
): TeamSetterDistribution {
  // La tabla JUNTA se arma sumando las dos fases ANTES de dividir: promediar
  // los porcentajes de cada fase daría cualquier cosa.
  const byReceptionGrade: Record<string, SetterGradeLine> = {};
  for (const grade of SETTABLE_GRADES) {
    byReceptionGrade[grade] = finalizeTargets(
      mergeTargets(
        phases.sideout.byGrade.get(grade) ?? new Map(),
        phases.transition.byGrade.get(grade) ?? new Map(),
      ),
      efficiency,
    );
  }
  return {
    name,
    byReceptionGrade,
    sideout: finalizePhase(phases.sideout, efficiency),
    transition: finalizePhase(phases.transition, efficiency),
  };
}

// ===========================================================================
// 3. Matriz de zonas (startZone × endZone) — ataque, saque y recepción
// ===========================================================================

/**
 * Los TRES mapas del mercado salen del mismo pliegue, porque las zonas viajan
 * en el contrato (`startZone`/`endZone`) para cualquier toque:
 *
 * | `skill` | qué mapea | cómo se leen las coordenadas |
 * |---|---|---|
 * | `attack` (default) | mapa de ataque | desde dónde remató → dónde cayó |
 * | `serve` | dirección de saque | desde dónde sacó → a qué zona mandó |
 * | `reception` | mapa de recepción | de dónde vino el saque → dónde se recibió |
 *
 * Los nombres de los contadores son los del ataque (`kills`, `blocked`) porque
 * son los del contrato y no se renombran, pero se leen según el fundamento: en
 * saque `kills` son aces y en recepción, recepciones perfectas.
 */
export type ZoneSkill = 'attack' | 'serve' | 'reception';

export const ZONE_SKILLS: readonly ZoneSkill[] = ['attack', 'serve', 'reception'];

export interface ZoneCell {
  startZone: number | null;
  endZone: number | null;
  total: number;
  /** Grado `#`: kills en ataque, aces en saque, perfectas en recepción. */
  kills: number;
  errors: number;
  /** Grado `/`: ataques bloqueados; en saque/recepción, el `/` del fundamento. */
  blocked: number;
  /** Ataque `(# − = − /) / total`; saque y recepción `(# − =) / total`. */
  efficiency: number;
  /** `# / total`; en recepción, POSITIVIDAD `(# + +) / total`. */
  perfectPct: number;
}

export interface TeamZoneMatrix {
  name: string;
  cells: ZoneCell[];
  total: number;
  kills: number;
  errors: number;
  blocked: number;
  efficiency: number;
  perfectPct: number;
}

export interface ZoneMatrix {
  matchId: string;
  generatedAt: string;
  setsIncluded: number[] | 'all';
  /** Fundamento mapeado (`'attack'` si no se pidió otro). */
  skill: ZoneSkill;
  player: number | null;
  home: TeamZoneMatrix;
  away: TeamZoneMatrix;
}

export interface ZoneMatrixOptions extends ProjectionOptions {
  /** Camiseta a aislar; sin esto, la matriz es de todo el equipo. */
  player?: number;
  /** Fundamento a mapear; por default `'attack'` (la matriz de siempre). */
  skill?: ZoneSkill;
}

const cellKey = (start: number | null, end: number | null): string => `${start ?? '-'}x${end ?? '-'}`;

function compareZone(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

interface ZoneCellAcc {
  start: number | null;
  end: number | null;
  counts: EvaluationCounts;
  total: number;
}

/**
 * Un toque sin zona codificada NO se descarta: cae en la celda con `null` en
 * la coordenada que falta, así el total de la matriz cierra con el de toques
 * del fundamento.
 */
export function buildZoneMatrix(
  all: readonly NormalizedEvent[],
  opts: ZoneMatrixOptions = {},
): ZoneMatrix {
  const meta = readMatchMeta(all);
  const counted = filterBySets(all, opts.sets);
  const skill: ZoneSkill = opts.skill ?? 'attack';
  // Sin filtro de rally no se recorren los rallies: costo y resultado de
  // siempre (mismo criterio que `@scout/stats`).
  const excluded =
    opts.previousRally !== undefined
      ? selectByPreviousRally(splitRallies(counted), opts.previousRally).excludedIds
      : new Set<string>();
  const acc: Record<TeamSide, Map<string, ZoneCellAcc>> = { home: new Map(), away: new Map() };

  for (const { event } of counted) {
    if (event.type !== 'touch') continue;
    if (excluded.has(event.id)) continue;
    const p = event.payload;
    if (p.skill !== skill) continue;
    if (opts.player !== undefined && p.player !== opts.player) continue;
    const start = p.startZone ?? null;
    const end = p.endZone ?? null;
    const map = acc[p.team];
    const key = cellKey(start, end);
    let cell = map.get(key);
    if (!cell) {
      cell = { start, end, counts: emptyCounts(), total: 0 };
      map.set(key, cell);
    }
    cell.counts[p.evaluation] += 1;
    cell.total += 1;
  }

  return {
    matchId: meta.matchId,
    generatedAt: new Date().toISOString(),
    setsIncluded: setsIncludedOf(opts.sets),
    skill,
    player: opts.player ?? null,
    home: finalizeZone(acc.home, meta.teams.home.name, skill, opts.efficiency),
    away: finalizeZone(acc.away, meta.teams.away.name, skill, opts.efficiency),
  };
}

function finalizeZone(
  map: Map<string, ZoneCellAcc>,
  name: string,
  skill: ZoneSkill,
  efficiency?: EfficiencyConfig,
): TeamZoneMatrix {
  // Los totales del equipo se acumulan por EVALUACIÓN (no sumando las
  // eficiencias de cada celda): la positividad de recepción necesita el `+`,
  // que no sobrevive al resumen de la celda.
  const totals = emptyCounts();
  let total = 0;
  const cells: ZoneCell[] = [...map.values()]
    .map((c) => {
      total += c.total;
      for (const e of EVALUATIONS) totals[e] += c.counts[e];
      return {
        startZone: c.start,
        endZone: c.end,
        total: c.total,
        kills: c.counts['#'],
        errors: c.counts['='],
        blocked: c.counts['/'],
        efficiency: efficiencyFor(skill, c.counts, c.total, efficiency),
        perfectPct: perfectPctFor(skill, c.counts, c.total, efficiency),
      };
    })
    .sort((a, b) => compareZone(a.startZone, b.startZone) || compareZone(a.endZone, b.endZone));

  return {
    name,
    cells,
    total,
    kills: totals['#'],
    errors: totals['='],
    blocked: totals['/'],
    efficiency: efficiencyFor(skill, totals, total, efficiency),
    perfectPct: perfectPctFor(skill, totals, total, efficiency),
  };
}

/**
 * Camisetas con al menos un toque codificado del fundamento, para el selector
 * de jugador. Depende del fundamento: los que rematan no son los que reciben.
 */
export function playersWithSkill(
  all: readonly NormalizedEvent[],
  side: TeamSide,
  skill: ZoneSkill = 'attack',
): number[] {
  const seen = new Set<number>();
  for (const { event } of all) {
    if (event.type !== 'touch') continue;
    if (event.payload.team !== side || event.payload.skill !== skill) continue;
    if (event.payload.player !== null) seen.add(event.payload.player);
  }
  return [...seen].sort((a, b) => a - b);
}

// ===========================================================================
// 4. Impacto de la recepción (sideout condicional al grado)
// ===========================================================================

export interface ReceptionImpactLine {
  evaluation: Evaluation;
  rallies: number;
  sideoutWon: number;
  sideoutPct: number;
}

export interface TeamReceptionImpact {
  name: string;
  byEvaluation: ReceptionImpactLine[];
  total: { rallies: number; sideoutWon: number; sideoutPct: number };
}

export interface ReceptionImpact {
  matchId: string;
  generatedAt: string;
  setsIncluded: number[] | 'all';
  home: TeamReceptionImpact;
  away: TeamReceptionImpact;
}

type ImpactAcc = Record<Evaluation, { rallies: number; won: number }>;

function emptyImpactAcc(): ImpactAcc {
  return {
    '#': { rallies: 0, won: 0 },
    '+': { rallies: 0, won: 0 },
    '!': { rallies: 0, won: 0 },
    '-': { rallies: 0, won: 0 },
    '/': { rallies: 0, won: 0 },
    '=': { rallies: 0, won: 0 },
  };
}

export function buildReceptionImpact(
  all: readonly NormalizedEvent[],
  opts: ProjectionOptions = {},
): ReceptionImpact {
  const meta = readMatchMeta(all);
  const rallies = closedRallies(
    selectByPreviousRally(splitRallies(filterBySets(all, opts.sets)), opts.previousRally).rallies,
  );
  const acc: Record<TeamSide, ImpactAcc> = { home: emptyImpactAcc(), away: emptyImpactAcc() };

  for (const rally of rallies) {
    for (const side of SIDES) {
      const reception = rally.touches.find(
        (t) => t.payload.team === side && t.payload.skill === 'reception',
      );
      if (!reception) continue;
      const cell = acc[side][reception.payload.evaluation];
      cell.rallies += 1;
      if (rally.winner === side) cell.won += 1;
    }
  }

  return {
    matchId: meta.matchId,
    generatedAt: new Date().toISOString(),
    setsIncluded: setsIncludedOf(opts.sets),
    home: finalizeImpact(acc.home, meta.teams.home.name),
    away: finalizeImpact(acc.away, meta.teams.away.name),
  };
}

function finalizeImpact(acc: ImpactAcc, name: string): TeamReceptionImpact {
  const byEvaluation = EVALUATIONS.map((evaluation) => {
    const cell = acc[evaluation];
    return {
      evaluation,
      rallies: cell.rallies,
      sideoutWon: cell.won,
      sideoutPct: ratio(cell.won, cell.rallies),
    };
  });
  const rallies = byEvaluation.reduce((sum, l) => sum + l.rallies, 0);
  const sideoutWon = byEvaluation.reduce((sum, l) => sum + l.sideoutWon, 0);
  return { name, byEvaluation, total: { rallies, sideoutWon, sideoutPct: ratio(sideoutWon, rallies) } };
}

// ===========================================================================
// 5. Evolución del marcador
// ===========================================================================

export interface ScorePoint {
  set: number;
  home: number;
  away: number;
  scorer: TeamSide;
}

/**
 * El marcador se RECUENTA desde `point.wonBy` en vez de leer el
 * `homeScore`/`awayScore` denormalizado: esos campos son cache de display y
 * quedan obsoletos apenas un `undo` revoca un punto anterior.
 */
export function buildScoreProgression(
  all: readonly NormalizedEvent[],
  opts: ProjectionOptions = {},
): ScorePoint[] {
  const counted = filterBySets(all, opts.sets);
  const running = new Map<number, { home: number; away: number }>();
  const out: ScorePoint[] = [];

  for (const { event, set } of counted) {
    if (event.type !== 'point') continue;
    let score = running.get(set);
    if (!score) {
      score = { home: 0, away: 0 };
      running.set(set, score);
    }
    const scorer = event.payload.wonBy;
    score[scorer] += 1;
    out.push({ set, home: score.home, away: score.away, scorer });
  }
  return out;
}

/** Serie por set, lista para el gráfico de líneas (incluye el 0-0 inicial). */
export interface ScoreSeries {
  set: number;
  points: ScorePoint[];
  maxScore: number;
}

export function groupScoreBySet(points: readonly ScorePoint[]): ScoreSeries[] {
  const bySet = new Map<number, ScorePoint[]>();
  for (const p of points) {
    const list = bySet.get(p.set);
    if (list) list.push(p);
    else bySet.set(p.set, [p]);
  }
  return [...bySet.entries()]
    .map(([set, list]) => ({
      set,
      points: [{ set, home: 0, away: 0, scorer: 'home' as TeamSide }, ...list],
      maxScore: list.reduce((max, p) => Math.max(max, p.home, p.away), 0),
    }))
    .sort((a, b) => a.set - b.set);
}

// ===========================================================================
// 6. Atribución de puntos: leer el desglose que trae el `MatchReport` del API
// ===========================================================================

/**
 * Esto NO es una proyección: el desglose lo calcula `buildMatchReport` en
 * `@scout/stats` y viaja en el JSON de `GET /report`. Lo que hay acá es el
 * LECTOR, porque el tipo `MatchReport` de `@scout/core` —el único que el
 * portal conoce— todavía no declara estos campos: `@scout/stats` los publica
 * extendiendo el contrato (`StatsTeamReport`), y extender el contrato es
 * exactamente lo que hace que un consumidor viejo siga compilando y no vea
 * nada.
 *
 * Se lee defensivo a propósito: contra un API anterior a esta ola (o contra el
 * 501 del package stub) los campos no vienen, y la pantalla tiene que decir
 * "no hay desglose" en vez de dibujar ceros que parecen un partido sin cargar.
 *
 * ## La identidad que hace que la columna CIERRE con el marcador
 *
 * ```
 *   total = byPlayer + unassigned + opponentErrors
 * ```
 *
 * - `byPlayer`: acción propia con camiseta — lo que suma la columna `Pts`.
 * - `unassigned`: acción propia sin camiseta codificada (el log lo permite).
 * - `opponentErrors`: los puntos que regaló el rival; no tienen autor propio y
 *   atribuírselos a alguien sería inventar estadística.
 */

/** Puntos por fundamento que anota: ace, kill, punto de bloqueo. */
export interface PointsBySkill {
  serve: number;
  attack: number;
  block: number;
}

export interface PlayerPoints extends PointsBySkill {
  /** `serve + attack + block`; es el mismo número que `pointsScored`. */
  total: number;
}

export interface TeamPoints {
  /** Puntos del marcador ganados por el equipo dentro del recorte. */
  total: number;
  byPlayer: number;
  unassigned: number;
  opponentErrors: number;
  /** Desglose de la acción propia (`byPlayer + unassigned`) por fundamento. */
  byOwnSkill: PointsBySkill;
}

/** Fundamentos que pueden anotar un punto directo. */
export const POINT_SKILLS: readonly (keyof PointsBySkill)[] = ['serve', 'attack', 'block'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Un contador del reporte, o `null` si el campo no vino o vino cualquier cosa. */
function readCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readPointsBySkill(value: unknown): PointsBySkill | null {
  if (!isRecord(value)) return null;
  const serve = readCount(value.serve);
  const attack = readCount(value.attack);
  const block = readCount(value.block);
  if (serve === null || attack === null || block === null) return null;
  return { serve, attack, block };
}

/** El desglose del equipo, o `null` si este API no lo publica. */
export function readTeamPoints(team: unknown): TeamPoints | null {
  if (!isRecord(team) || !isRecord(team.points)) return null;
  const p = team.points;
  const total = readCount(p.total);
  const byPlayer = readCount(p.byPlayer);
  const unassigned = readCount(p.unassigned);
  const opponentErrors = readCount(p.opponentErrors);
  const byOwnSkill = readPointsBySkill(p.byOwnSkill);
  if (
    total === null ||
    byPlayer === null ||
    unassigned === null ||
    opponentErrors === null ||
    byOwnSkill === null
  ) {
    return null;
  }
  return { total, byPlayer, unassigned, opponentErrors, byOwnSkill };
}

/** El desglose ace/kill/bloqueo de un jugador, o `null` si no vino. */
export function readPlayerPoints(player: unknown): PlayerPoints | null {
  if (!isRecord(player)) return null;
  const bySkill = readPointsBySkill(player.points);
  if (!bySkill) return null;
  const declared = isRecord(player.points) ? readCount(player.points.total) : null;
  return { ...bySkill, total: declared ?? bySkill.serve + bySkill.attack + bySkill.block };
}

/**
 * ¿Cierra la identidad? Es la pregunta que el DT hace en voz alta cuando suma
 * la columna delante del cliente, así que la pantalla la contesta sola en vez
 * de dejar que la sume él.
 */
export function pointsAddUp(points: TeamPoints): boolean {
  return points.byPlayer + points.unassigned + points.opponentErrors === points.total;
}

/** Todo junto: una sola pasada del log para las cinco vistas. */
export interface DerivedMatchViews {
  meta: MatchMeta;
  rotation: RotationReport;
  setter: SetterDistribution;
  zones: ZoneMatrix;
  reception: ReceptionImpact;
  score: ScorePoint[];
}

export { countTotal, efficiencyFor, emptyCounts, normalizeLog, perfectPctFor, ratio };
